import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

export default function SubscribeDialog({ portal, availableScrapers, open, onOpenChange, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [chosenPortal, setChosenPortal] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: "",
      password: "",
    },
  });

  // When portal is preselected (clicked from table row), use it directly
  useEffect(() => {
    if (portal) {
      setChosenPortal(portal);
    } else {
      setChosenPortal(null);
    }
  }, [portal, open]);

  const activePortal = chosenPortal;
  const showDropdown = !portal && availableScrapers && availableScrapers.length > 0;

  const onSubmit = async (data) => {
    if (!activePortal) {
      toast.error("Please select a portal first");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/catalog/subscribe", {
        catalog_id: activePortal.catalog_id,
        username: data.username,
        password: data.password,
      });
      toast.success(`Subscribed to ${activePortal.name || activePortal.portal_name || "portal"} successfully`);
      reset();
      setChosenPortal(null);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to subscribe. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (isOpen) => {
    if (!isOpen) {
      reset();
      setChosenPortal(null);
    }
    onOpenChange(isOpen);
  };

  const handlePortalSelect = (catalogId) => {
    const found = availableScrapers?.find((s) => s.catalog_id === catalogId);
    setChosenPortal(found || null);
  };

  const title = activePortal
    ? `Subscribe to ${activePortal.name || activePortal.portal_name || "Portal"}`
    : "Subscribe to a Portal";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {showDropdown && !activePortal
              ? "Select a portal from the catalog, then enter your credentials."
              : "Enter your portal credentials to start automatic document scraping."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {showDropdown && (
            <div className="space-y-2">
              <Label>Portal</Label>
              <Select onValueChange={handlePortalSelect} value={chosenPortal?.catalog_id || ""}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a portal..." />
                </SelectTrigger>
                <SelectContent>
                  {availableScrapers.map((s) => (
                    <SelectItem key={s.catalog_id} value={s.catalog_id}>
                      {s.name || s.portal_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="username">Username (email)</Label>
            <Input
              id="username"
              type="email"
              placeholder="your@email.com"
              {...register("username", {
                required: "Username is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Enter a valid email address",
                },
              })}
            />
            {errors.username && (
              <p className="text-xs text-red-500">{errors.username.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your portal password"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 1,
                  message: "Password cannot be empty",
                },
              })}
            />
            {errors.password && (
              <p className="text-xs text-red-500">{errors.password.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || (!activePortal && showDropdown)}
              className="text-white"
              style={{ backgroundColor: BRAND.bg }}
              onMouseEnter={(e) =>
                !submitting && (e.currentTarget.style.backgroundColor = BRAND.hover)
              }
              onMouseLeave={(e) =>
                !submitting && (e.currentTarget.style.backgroundColor = BRAND.bg)
              }
            >
              {submitting ? "Subscribing..." : "Subscribe"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
